import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, History } from "lucide-react";
import { BranchHistoryRow } from "./BranchHistoryRow";

const PRESETS = ["all", "m1", "m3", "y1", "custom"];
const DAYS = { m1: 30, m3: 90, y1: 365 };

const withinRange = (iso, from, to) => {
  const stamp = new Date(iso).getTime();
  if (from && stamp < new Date(from).getTime()) return false;
  if (to && stamp > new Date(to).getTime() + 86400000) return false;
  return true;
};

// Collapsible change-log with period filters (month/quarter/year/custom).
export const HistoryPanel = ({ title, hint, emptyLabel, loader, rowNs, testId }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (!open || items !== null) return;
    let alive = true;
    loader()
      .then((res) => alive && setItems(res || []))
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [open, items, loader]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (preset === "all") return items;
    if (preset === "custom") return items.filter((e) => withinRange(e.at, from, to));
    const since = new Date(Date.now() - DAYS[preset] * 86400000).toISOString();
    return items.filter((e) => e.at >= since);
  }, [items, preset, from, to]);

  return (
    <div className="rounded-3xl bg-background shadow-neu-sm" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        className={`flex w-full items-center gap-3 px-5 py-4 transition ${open ? "rounded-t-3xl shadow-neu-in" : "hover:-translate-y-0.5"}`}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <History className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1 text-start">
          <span className="block text-sm font-bold">{title}</span>
          <span className="block text-[11px] text-muted-foreground">
            {items ? t("history.count", { n: items.length }) : hint}
          </span>
        </span>
        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="border-t border-border/50 px-5 py-4">
          <div className="flex flex-wrap gap-2" data-testid={`${testId}-filters`}>
            {PRESETS.map((key) => (
              <button key={key} type="button" data-testid={`${testId}-preset-${key}`}
                onClick={() => setPreset(key)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition ${preset === key ? "text-primary shadow-neu-in" : "text-muted-foreground shadow-neu-sm hover:text-foreground"}`}>
                {t(`history.preset.${key}`, { defaultValue: key })}
              </button>
            ))}
          </div>
          {preset === "custom" ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testId}-from`}
                className="h-10 rounded-2xl bg-background px-3 text-xs shadow-neu-in" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testId}-to`}
                className="h-10 rounded-2xl bg-background px-3 text-xs shadow-neu-in" />
            </div>
          ) : null}

          {items === null ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("app.loading")}</p>
          ) : filtered.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{emptyLabel}</p>
          ) : (
            <ul className="mt-4 grid gap-3">
              {filtered.map((e) => <BranchHistoryRow key={e.id} entry={e} ns={rowNs} />)}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};
